import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { resolveMedia } from "@/lib/api";
import { Home, Sun, Briefcase, Car, HeartHandshake, Compass, Heart, ChevronRight } from "lucide-react";

export const SERVICE_ICONS = { home: Home, sun: Sun, briefcase: Briefcase, car: Car, "heart-handshake": HeartHandshake, compass: Compass, heart: Heart };

export const PHOTOS = {
  hero: "https://images.unsplash.com/photo-1765896387398-1e1ae8d2eb85?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  transport: "https://images.unsplash.com/photo-1732194439331-08ec61c4df4f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  community: "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  care: "https://images.unsplash.com/photo-1576765607924-3f7b8410a787?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  facility: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

export function Container({ children, className = "" }) {
  return <div className={`max-w-[1280px] mx-auto px-5 sm:px-8 ${className}`}>{children}</div>;
}

export const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.6 } };

export function Reveal({ children, delay = 0, className = "" }) {
  return <motion.div {...fade} transition={{ duration: 0.55, delay }} className={className}>{children}</motion.div>;
}

export function Eyebrow({ children, gold }) {
  return <p className={`text-xs uppercase tracking-[0.2em] font-bold ${gold ? "text-gold" : "text-primary"}`}>{children}</p>;
}

export function SectionHeading({ eyebrow, title, sub, gold, center }) {
  return (
    <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""}`}>
      {eyebrow && <Eyebrow gold={gold}>{eyebrow}</Eyebrow>}
      <h2 className="font-heading text-4xl lg:text-5xl font-bold tracking-tight mt-3 leading-[1.1]">{title}</h2>
      {sub && <p className="text-[17px] text-muted-foreground mt-4 leading-[1.7]">{sub}</p>}
    </div>
  );
}

export function Breadcrumbs({ items }) {
  return (
    <nav className="flex items-center gap-1.5 text-sm text-cream/70" aria-label="Breadcrumb">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {it.to ? <Link to={it.to} className="hover:text-cream">{it.label}</Link> : <span className="text-cream">{it.label}</span>}
          {i < items.length - 1 && <ChevronRight className="w-3.5 h-3.5" />}
        </span>
      ))}
    </nav>
  );
}

export function PageHero({ eyebrow, title, sub, image, breadcrumbs }) {
  return (
    <section className="relative bg-foreground text-cream overflow-hidden">
      {image && <img src={resolveMedia(image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />}
      <div className="absolute inset-0 bg-gradient-to-r from-foreground via-foreground/90 to-foreground/60" />
      <Container className="relative py-16 lg:py-24">
        {breadcrumbs && <div className="mb-5"><Breadcrumbs items={breadcrumbs} /></div>}
        {eyebrow && <p className="text-xs uppercase tracking-[0.2em] font-bold text-gold">{eyebrow}</p>}
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-3 leading-[1.05] max-w-3xl">{title}</h1>
        {sub && <p className="text-[18px] text-cream/80 mt-5 leading-[1.7] max-w-2xl">{sub}</p>}
      </Container>
    </section>
  );
}

export function CtaBand({ title = "Ready to get started?", sub = "Request services or make a referral — our team will reach out shortly." }) {
  return (
    <section className="bg-primary text-primary-foreground">
      <Container className="py-16 lg:py-20 flex flex-col lg:flex-row items-center justify-between gap-8">
        <div className="max-w-xl">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">{title}</h2>
          <p className="text-primary-foreground/85 mt-3 text-[17px]">{sub}</p>
        </div>
        <div className="flex flex-wrap gap-3 shrink-0">
          <Link to="/request-services"><button className="rounded-full bg-cream text-primary font-semibold h-13 min-h-[52px] px-8 hover:bg-white transition-colors" data-testid="cta-request">Request Services</button></Link>
          <Link to="/referral"><button className="rounded-full border-2 border-cream/60 text-cream font-semibold h-13 min-h-[52px] px-8 hover:bg-cream/10 transition-colors" data-testid="cta-referral">Make a Referral</button></Link>
        </div>
      </Container>
    </section>
  );
}
